/**
 * Created by rathompson on 10/13/23
 * File: AcquireTenantModal.tsx
 * Project: cloud-mgmt-modules ©
 * ©
 */


import type { VFC } from 'react'
import type { Tsg } from '@sparky/framework'
import {
  Body,
  Bold,
  Button,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalProps, useIsMounted
} from '@panwds/react-ui'
import { useCallback, useEffect, useState } from 'react'
import StringTemplate from '../../../common/utils/StringTemplate'
import {
  BUTTON_ACQUIRE_TENANT,
  ERROR_INVALID_INFO,
  ERROR_TSG_ID_INVALID,
  FORM_FIELD_CONTACT_EMAILS,
  FORM_FIELD_TSG_ID,
  LABEL_ACQUISITION_KEY,
  LABEL_PROVIDE_EXTERNAL_TENANT,
  TITLE_ACQUIRE_TENANT
} from '../../constants/LabelConstants'
import { Form, Input, SubmitButton, ChipInput } from '@panwds/react-form'
import { BUTTON_CANCEL } from '../../../common/constants/LabelConstants'
import { isValidTsgId } from '../../../activation/components/common/utils'
import { safeSet, validateEmail } from '../../../common/utils/Utils'
import { useFormContext } from 'react-hook-form'
import TenancyService from '../../services/TenancyService'
import ScreenLoader from '../../../activation/components/common/ScreenLoader'
import { Modal } from '../../../common/components/modal/Modal'

const AcquireTenantForm: VFC<{ tsg: Tsg, onClose: any, error: string }> = ({ tsg, onClose, error }) => {

  const { formState, trigger } = useFormContext()
  const { isValid } = formState

  return (
    <>
      <ModalBody addClassName='cm-flex cm-flex-col cm-gap-3'>
        {error && <Bold appearance='semibold' addClassName='tw-text-error'>{error}</Bold>}
        <Body>{StringTemplate(LABEL_PROVIDE_EXTERNAL_TENANT, [tsg.display_name])}</Body>
        <div className='cm-flex cm-flex-col cm-gap-1'>
          <Input
            name={'target_tsg_id'}
            label={FORM_FIELD_TSG_ID}
            register={{
              validate: value => isValidTsgId(value) || ERROR_TSG_ID_INVALID,
              onChange: () => trigger('tsg_id')
            }}
          />
        </div>
        <div className='cm-flex cm-flex-col cm-gap-1'>
          <ChipInput
            allowArbitraryItems
            items={[]}
            label={FORM_FIELD_CONTACT_EMAILS}
            name='contacts'
            register={{
              validate: values => values.every(({ value }: any) => validateEmail(value)) || ERROR_INVALID_INFO,
              onChange: () => trigger('contacts')
            }}
          />
        </div>
        <Body>{LABEL_ACQUISITION_KEY}</Body>
      </ModalBody>
      <ModalFooter>
        <Button onClick={onClose}>{BUTTON_CANCEL}</Button>
        <SubmitButton disabled={!isValid}>{BUTTON_ACQUIRE_TENANT}</SubmitButton>
      </ModalFooter>
    </>
  )
}

const AcquireTenantModal: VFC<ModalProps & {
  tsg: Tsg,
  closeModal: any,
  onSuccess: any
}> = ({ tsg, isOpen, closeModal, onSuccess }) => {

  const isMounted = useIsMounted()
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<string>('')

  const onClose = useCallback(() => {
    closeModal()
  }, [closeModal])

  const title = StringTemplate(TITLE_ACQUIRE_TENANT, [tsg.display_name])

  useEffect(() => {
    const modalMask = document.getElementsByClassName('tw-bg-blue-steel-700 tw-fixed tw-inset-0 tw-z-10 tw-transition-opacity tw-duration-200 tw-opacity-80')
    if (modalMask && modalMask.length) {
      (modalMask[0] as HTMLElement).style.zIndex = '1051'
    }
    else {
      const mask = document.getElementsByClassName('tw-bg-gray-900 tw-fixed tw-inset-0 tw-z-10 tw-transition-opacity tw-duration-200 tw-opacity-75')
      if (mask && mask.length) {
        (modalMask[0] as HTMLElement).style.zIndex = '1051'
      }
    }
  }, [isOpen])

  const onSubmit = useCallback(async ({ target_tsg_id, contacts: _contacts }) => {
    try {
      safeSet(setLoading, true, isMounted.current)
      const contacts = _contacts.map(({ value }: any) => value)
      const payload = { target_tsg_id, contacts }
      const { data: acquisition } = await TenancyService.acquisitions.create(tsg.id, payload)
      onClose()
      await onSuccess(acquisition)
    }
    catch (error: any) {
      safeSet(setError, error.message, isMounted.current)
    }
    finally {
      safeSet(setLoading, false, isMounted.current)
    }
  }, [onClose, onSuccess, tsg.id, isMounted])

  return (
    <Modal {...{ isOpen, onClose } as ModalProps}>
      <Form onSubmit={onSubmit} mode='onChange'>
        {loading && <ScreenLoader appearance='light' />}
        <ModalHeader {...{ title }}/>
        <AcquireTenantForm {...{ tsg, onClose, error }} />
      </Form>
    </Modal>
  )
}

export default AcquireTenantModal
